import { useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LayoutDashboard, Boxes, Inbox, Compass, Star, Settings, LogOut, ExternalLink, Loader2, Menu, Users, BarChart3, Share2, Handshake, Briefcase, FileUser, Table2 } from "lucide-react";
import { Logo } from "../../components/site/Logo";
import { useAuth } from "../../lib/auth";
import { http } from "../../lib/api";

const LINKS = [
  ["/admin", "Overview", LayoutDashboard, null, false],
  ["/admin/inventory", "Inventory", Boxes, null, false],
  ["/admin/inquiries", "Inquiries", Inbox, "overdue", false],
  ["/admin/sourcing", "Sourcing Requests", Compass, "new_sourcing", false],
  ["/admin/opportunities", "Business Opportunities", Handshake, "new_opportunities", false],
  ["/admin/reports", "Weekly Report", BarChart3, null, true],
  ["/admin/shared-pdfs", "Shared PDFs", Share2, null, true],
  ["/admin/reviews", "Client Reviews", Star, "pending_reviews", true],
  ["/admin/careers", "Careers · Jobs", Briefcase, null, true],
  ["/admin/careers/details", "Job Details", Table2, null, true],
  ["/admin/careers/applications", "Job Applications", FileUser, "new_applications", true],
  ["/admin/users", "Team & Access", Users, null, true],
  ["/admin/settings", "Settings", Settings, null, false],
];

export const OwnerOnly = ({ children }) => {
  const { user } = useAuth();
  if (user?.role !== "owner")
    return <p className="border border-dashed border-white/20 p-12 text-center text-slate-300" data-testid="owner-only-message">This section is available to the owner only.</p>;
  return children;
};

export const useStats = () => useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await http.get("/admin/stats")).data });

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { data: stats } = useQuery({ queryKey: ["admin-stats"], queryFn: async () => (await http.get("/admin/stats")).data, enabled: !!user });

  if (user === null) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const nav = (
    <nav className="flex flex-col gap-1">
      {LINKS.filter((l) => !l[4] || user.role === "owner").map(([to, label, Icon, key]) => (
        <NavLink key={to} to={to} end={to === "/admin" || to === "/admin/careers"} onClick={() => setOpen(false)}
          className={({ isActive }) => `flex items-center gap-3 px-4 py-3 text-sm transition-colors ${isActive ? "bg-[var(--gold)] text-[#0a0d14] font-semibold" : "text-slate-200 hover:bg-white/5"}`}
          data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <Icon className="w-4 h-4" /> <span className="flex-1">{label}</span>
          {key && stats?.[key] > 0 && <span className="font-mono text-[0.65rem] bg-rose-500 text-white px-1.5 py-0.5">{stats[key]}</span>}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[260px_1fr]" data-testid="admin-layout">
      <aside className="hidden lg:flex flex-col border-r hairline bg-[#080a10] p-5 sticky top-0 h-screen">
        <Link to="/" className="px-2 py-3 mb-8"><Logo /></Link>
        {nav}
        <div className="mt-auto space-y-1">
          <Link to="/" target="_blank" className="flex items-center gap-3 px-4 py-3 text-sm text-slate-300 hover:text-gold"><ExternalLink className="w-4 h-4" /> View website</Link>
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-300 hover:text-gold" data-testid="admin-logout-button"><LogOut className="w-4 h-4" /> Sign out</button>
          <p className="px-4 pt-3 text-xs text-slate-400 break-all">{user.name} · <span className="capitalize text-gold">{user.role}</span><br />{user.email}</p>
        </div>
      </aside>
      <div className="lg:hidden flex items-center justify-between border-b hairline px-5 py-4 bg-[#080a10] sticky top-0 z-40">
        <Logo />
        <button onClick={() => setOpen(!open)} className="w-10 h-10 grid place-items-center border border-white/15" data-testid="admin-mobile-menu-toggle"><Menu className="w-5 h-5" /></button>
      </div>
      {open && (
        <div className="lg:hidden border-b hairline bg-[#080a10] p-4 space-y-2" data-testid="admin-mobile-menu">
          {nav}
          <button onClick={logout} className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-300" data-testid="admin-mobile-logout-button"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
      )}
      <main className="p-5 md:p-10 min-w-0" key={pathname}><Outlet /></main>
    </div>
  );
}

export const AdminTitle = ({ title, sub, children }) => (
  <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
    <div>
      <p className="eyebrow">Staff Dashboard</p>
      <h1 className="font-display text-4xl md:text-5xl mt-2">{title}</h1>
      {sub && <p className="text-sm text-slate-300 mt-2">{sub}</p>}
    </div>
    {children && <div className="flex flex-wrap gap-3">{children}</div>}
  </div>
);
